import React from "react";
import styled, { css } from "styled-components";
import { useRouter } from "next/router";
import {
  Button,
  ButtonProps,
  Icon,
  IconName,
  Theme,
  colors,
  layout,
  media,
} from "@literati/public-ui-shared-react";
import StepFormButton from "components/Step/StepFormButton";

/** This is the className that will be pre-pended to all of StepFooter's inner component classNames */
export const STEP_FOOTER_CLASS_BASE = "step-footer";

/** These are all of the internal classNames that are used within the StepFooter component */
export const StepFooterClassName = {
  BASE: STEP_FOOTER_CLASS_BASE,
  CONTENT: `${STEP_FOOTER_CLASS_BASE}-content`,
} as const;

export const FOOTER_HEIGHT_PX = "84px";
export const FOOTER_HEIGHT = 84;

const Footer = styled.footer`
  display: flex;
  justify-content: center;

  z-index: 50;
  width: 100vw;
  height: ${FOOTER_HEIGHT_PX};

  background: ${colors.white};
  box-shadow: 0px 0px 15px ${colors.newsprint1};
`;

const Content = styled.div<{ $suppressBackButton: boolean }>`
  ${layout.contentSpacing};

  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  ${media.md} {
    justify-content: ${({ $suppressBackButton }) =>
      $suppressBackButton ? "flex-end" : "space-between"};
  }
`;

const BackButtonContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BackButton = styled(Button)<{ $suppressBackButton: boolean }>`
  display: none;
  width: auto;
  padding: 0 1rem;
  margin: 0 0 0 -1rem;

  ${media.md} {
    display: ${({ $suppressBackButton }) =>
      $suppressBackButton ? "none" : "block"};
  }
`;

const BackButtonText = styled.span`
  margin: 0 0 0 1rem;
`;

const buttonStyles = css`
  width: 100%;
  max-width: 100%;

  ${media.md} {
    width: 271px;
    max-width: 271px;
  }
`;

const NextButton = styled(Button)`
  ${buttonStyles}
`;

const NextFormButton = styled(StepFormButton)`
  ${buttonStyles}
`;

export interface StepFooterProps {
  asForm?: boolean;
  backButtonProps?: ButtonProps;
  className?: string;
  nextButtonProps?: ButtonProps;
  suppressBackButton?: boolean;
}

/**
 * @note - A UX decision was made to keep form submit buttons enabled by default. (Feb '24)
 */
export const StepFooter = ({
  asForm = false,
  backButtonProps = {},
  className = "",
  nextButtonProps,
  suppressBackButton = false,
}: StepFooterProps) => {
  const cls = StepFooterClassName;
  const router = useRouter();

  const NextButtonComponent = asForm ? NextFormButton : NextButton;

  return (
    <Theme>
      <Footer className={`${cls.BASE} ${className}`}>
        <Content
          className={cls.CONTENT}
          $suppressBackButton={suppressBackButton}
        >
          <BackButton
            onClick={() => router.back()}
            $suppressBackButton={suppressBackButton}
            variant="unstyled"
            type="button"
            {...backButtonProps}
          >
            {backButtonProps?.children || (
              <BackButtonContent>
                <Icon name={IconName.CHEVRON_LEFT} />
                <BackButtonText>Back</BackButtonText>
              </BackButtonContent>
            )}
          </BackButton>
          <NextButtonComponent
            disabled={asForm ? false : undefined}
            {...nextButtonProps}
          >
            {nextButtonProps?.children || "Continue"}
          </NextButtonComponent>
        </Content>
      </Footer>
    </Theme>
  );
};

export default StepFooter;
